import React from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled } from '@mui/material/styles';
import PublicFormPage from '../../../forms/PublicFormPage';
import FormFlowPage from '../../../forms/FormFlow/FormFlowPage';

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  height: '90%',
  backgroundColor: theme.palette.background.paper,
  boxShadow: 24,
  borderRadius: '10px',
  padding: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  maxHeight: '56px',
  backgroundColor: theme.palette.background.subtle,
  color: theme.palette.text.primary,
}));

const StyledToolbar = styled(Toolbar)({
  minHeight: '56px',
  height: '56px',
  padding: '0 16px',
});

const ContentWrapper = styled(Box)({
  flexGrow: 1,
  overflow: 'auto',
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  flexGrow: 1,
  marginRight: '8px',
  '& .MuiInputBase-root': {
    height: '36px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '18px',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const ActionButton = styled(IconButton)({
  width: '36px',
  height: '36px',
  marginLeft: '8px',
});

const LoadingWrapper = styled(Box)({
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '12px',
});

/**
 * Preview modal for v2 form-likes (form_step / dynamic_form / dynamic_review_task).
 *
 * v2 forms are first-class React routes inside admin-dashboard (PublicFormPage,
 * FormFlowPage), so we render them directly — no iframe. An iframe forced the
 * whole form surface to re-bootstrap inside its own document, which in practice
 * meant the admin would see the standalone /dynamic-form UI load first, then
 * refresh into the flow shell once /form-flow/start-session resolved (5+
 * seconds on cold trigger). Rendering inline lets the *caller* bootstrap the
 * FlowSession before opening the modal, so we only ever mount one UI.
 *
 * Render modes:
 *  - {@code formSessionId} provided → mount {@link FormFlowPage} directly with
 *    the resolved FlowSession id. Caller pre-bootstrapped via
 *    POST /form-flow/start-session (prod) or /start-test-session (test) and the
 *    author lands on the stepper instantly. {@code isTest} selects which
 *    collection the flow hooks read from.
 *  - {@code formSessionId} absent → mount {@link PublicFormPage} for standalone
 *    single-form previews (components that aren't part of a flow).
 *  - {@code loading=true} → spinner (caller's bootstrap is in flight).
 */
const DynamicFormModal = ({
  open,
  onClose,
  componentId,
  preview = false,
  formSessionId = null,
  isTest = false,
  loading = false,
}) => {
  const origin = window.location.origin.replace(/\/+$/, '');
  const displayUrl = formSessionId
    ? `${origin}${isTest ? '/form-flow-test' : '/form-flow'}/${formSessionId}`
    : `${origin}/dynamic-form/${componentId}${preview ? '?preview=true' : ''}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(displayUrl);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="dynamic-form-modal"
      aria-describedby="dynamic-form-in-modal"
    >
      <ModalBox>
        <StyledAppBar position="static" elevation={0}>
          <StyledToolbar>
            <StyledTextField
              value={displayUrl}
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
            />
            <ActionButton color="inherit" onClick={copyUrl} aria-label="copy url">
              <ContentCopyIcon />
            </ActionButton>
            <ActionButton
              color="inherit"
              onClick={() => window.open(displayUrl, '_blank', 'noopener,noreferrer')}
              aria-label="open in new tab"
            >
              <OpenInNewIcon />
            </ActionButton>
            <ActionButton edge="end" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </ActionButton>
          </StyledToolbar>
        </StyledAppBar>
        <ContentWrapper>
          {open && loading && (
            <LoadingWrapper>
              <CircularProgress size={28} />
              <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
                Preparing preview…
              </Box>
            </LoadingWrapper>
          )}
          {open && !loading && formSessionId && (
            <FormFlowPage testMode={isTest} formSessionId={formSessionId} />
          )}
          {open && !loading && !formSessionId && componentId && (
            <PublicFormPage
              componentId={componentId}
              preview={preview}
              embedded
            />
          )}
        </ContentWrapper>
      </ModalBox>
    </Modal>
  );
};

export default DynamicFormModal;
